const _ = require("lodash");
const Big = require("big.js");

const AT = require("../../core/vault/ActionTypes");
const {setIn, extendIn} = require("../../../shared/ImmHelpers");
const OpsUser = require("../../core/vault/OpsUser");
const OpsCheckoutForm = require("./OpsCheckoutForm");
const StoreUtil = require("../../../shared/StoreUtil");
const CouponUtil = require("../../../shared/CouponUtil");
const StoreBundleUtil = require("../../../shared/StoreBundleUtil");
const CartItemsUtil = require("../../../shared/CartItemsUtil");
const StringUtil = require("../../../shared/StringUtil");
const BookstoreUtil = require("../../../shared/BookstoreUtil");
const ErrorUtil = require("../../../shared/ErrorUtil");
const {ITEM_LIMIT} = require("../../../shared/Constants");

const EMPTY_CART_SUGGESTIONS = [
	"ba-online",
	"shirt-beast-youth-small",
	"beast-1a",
	"beast-1b",
];

/*
Cart shape {
  userID: see TODO below,
  cartID,
  items: an array of objects with required props sku and qty. The prop
    options, an object for any other choices for the item (like size or
    color), is optional. objects must be unique without their qty, so there
    can be no 2 with the same skus and the same options
    example: [
      {sku: "beast-3a", quantity: 1, options: {}},
      {sku: "beast-3a-practice", quantity: 1, options: {}},
      {sku: "tshirt-grogg", quantity: 3, options: {size: "small", color: "blue"}},
      {sku: "tshirt-grogg", quantity: 2, options: {size: "large", color: "blue"}},
    ]
  itemSuggestions: an array of length 4 (or less if fewer than 4 books are
    not in the cart) of skus for suggested items
  prices: an object with keys subtotal, total, tax, couponApplied, and shipping
    that are all strings (result of Big toFixed) if their values are known and
    null if they are not known yet (i.e. require a backend call to be
    calculated)
  form: OpsCheckoutForm object,
  userDidSetSubscribe: boolean, whether the user manually set the "subscribe
    option". Not sent to the server.
  subscribeOnCheckout: boolean, whether to subscribe the user to our mailing
    list when they checkout,
  coupon: a coupon object validated by the backend or null if none, has code
    property along with the properties that CouponUtil requires,
  isDoingFirstSave: true when a new cart (one without an ID, so not one
    loaded in from local storage) first gets an update to its items and
    starts saving to the backend
  needsSave: true if another save to the backend was triggered and deferred
    while isDoingFirstSave is still true or while isPreparingOrder was true
  lastItemsUpdate: the Date.now() value with when the cart's items were last
    updated
  saveSuccessUpdateTime: the lastItemsUpdate of the most recent cart that
    successfully saved
  saveFailUpdateTime: the lastItemsUpdate of the most recent cart that
    failed to save, 0 iff more recent saves have succeeded
  savesFailing: true if saves have failed since the last successful save
  isVerifyingAccount: Date.now of verifying account call, false if not
    running
  accountExists: true if the entered account info is known to exist since
    last change
  accountVerified: true if the account info has been verified on back end
    since last change
  isGettingCoupon: Date.now of get coupon call, false if not running
  lastCouponCode: a currently-unused coupon code that user entered previously
    which is used to prefill the coupon modal if opened,
  isPreparingOrder: false if the prepareOrder endpoint is not running, and
    else the date.now() value of when prepareOrder was started
  addressVerified: true if prepareOrder has verified the address since last
    change
  prepareOrderFails: the number of failed attempts to verify shipping
  lastCancelledPrepareOrder: Date.now value of cancelling a sent
    prepareOrder call, else null. Used only in an obscure error condition.
  isPlacingOrder: false if the placeOrder endpoint is not running, and else
    the date.now() value of when placeOrder was started
  isStartingPaypal: true if we are currently trying to retrieve paypal info
  onPaypalFlow: true if user's checkout page is using paypal steps
  paypalPaymentID: id required for paypal flow
  paypalPayerID: id required for paypal flow
  academyCampusURL: URL to a local AoPS Academy Campus
}
*/

// IMPORTANT GOTCHA: When accessing form data in a cart from outside this file,
// avoid using cart.form directly and use OpsCart.getFormSection, which
// accounts for things like shippingForBilling.

// Cart TODOs: anything associated with attaching carts to user accounts,
// loading in carts associated with userIDs

const init = function(now) {
	const cart = {
		userID: null,
		cartID: null,
		items: [],
		itemSuggestions: EMPTY_CART_SUGGESTIONS,
		prices: {
			subtotal: "0.00",
			couponApplied: "0.00",
			shipping: null,
			tax: null,
			total: null,
		},
		form: OpsCheckoutForm.init("cart", now),
		userDidSetSubscribe: false,
		subscribeOnCheckout: true,
		coupon: null,

		isDoingFirstSave: false,
		needsSave: false,
		lastItemsUpdate: 0,
		saveSuccessUpdateTime: 0,
		saveFailUpdateTime: 0,
		savesFailing: false,

		isVerifyingAccount: false,
		accountVerified: false,
		accountExists: false,
		isPreparingOrder: false,
		addressVerified: false,
		prepareOrderFails: 0,
		lastCancelledPrepareOrder: false,
		isPlacingOrder: false,

		isStartingPaypal: false,
		onPaypalFlow: false,
		paypalPayerID: null,
		paypalPaymentID: null,

		academyCampusURL: null,
	};

	return cart;
};

// Resets the cart by getting rid of all information in cart except
// cartID, items, and lastFinishedCart
const clearNonItems = function(cart, now, user) {
	if (!cart) return init(now);
	const resetCart = _.extend(init(now), {
		cartID: cart.cartID,
		items: cart.items,
		lastFinishedCart: cart.lastFinishedCart,
	});
	return postItemsUpdate(resetCart, cart.lastItemsUpdate, true, user);
};

const fixCart = function(cart, now, user) {
	if (!cart) return init(now);

	const verified = fixCartItems(cart.items, cart);
	if (verified.length < cart.items.length) {
		cart = setIn(cart, "items", verified);
		cart = postItemsUpdate(cart, now, true, user);
	}
	return cart;
};

// If true, view should show a loading indicator over entire page.
const shouldBlockPage = function(cart) {
	return cart && (cart.isPreparingOrder || cart.isPlacingOrder);
};

const updateShippingAddressFromPaypal = function(cart, newAddress = {}) {
	newAddress = StoreUtil.cleanAddress(newAddress);
	return updateFormData(cart, "shipping", newAddress);
};

const updateFormData = function(cart, section, vals) {
	const options = {
		useBillingForTaxes: !needsShipping(cart),
		needsRecurringBilling: needsRecurringBilling(cart),
		shouldSkipPhoneValidation: shouldSkipPhoneValidation(cart),
	};
	const updates = OpsCheckoutForm.updateData(cart.form, section, vals, options);
	if (!updates.responseFlags.hadUpdates) return cart;

	cart = setIn(cart, "form", updates.form);
	if (updates.responseFlags.verifyAddress) {
		cart = setIn(cart, "addressVerified", false);
	}
	if (updates.responseFlags.verifyAccount) {
		cart = setIn(cart, "accountVerified", false);
		cart = setIn(cart, "accountExists", false);
	}
	if (updates.responseFlags.updatePrices) {
		cart = updatePrices(cart);
	}

	return cart;
};

// Adds any extra fees like the Colorado state tax (applied if shipping to Colorado address)
// by adding the fee as an 'item' in the cart.
const addExtraCartItems = function(cart, section, now) {
	if (section !== "shipping") {
		return cart;
	}

	const state = _.get(cart, "form.data.shipping.state");
	const country = _.get(cart, "form.data.shipping.country");
	const applicableStates = Object.keys(StoreUtil.STATE_SHIPPING_FEES);
	const isSubjectToStateTax =
		needsShipping(cart) &&
		StoreUtil.isCountryUS(country) &&
		state &&
		applicableStates.includes(state);

	// Helper checks for any existing shipping fee items, and removes any that exist.
	const shippingFeeItems = StoreUtil.getShippingFeeItems(cart.items);
	const removeShippingFeesFromCart = () => {
		if (shippingFeeItems.length) {
			shippingFeeItems.forEach(item => {
				cart = removeItem(cart, now, item);
			});
		}
	};

	// If cart is not subject to any state tax (most cases), return with any shipping fees removed.
	if (!isSubjectToStateTax) {
		removeShippingFeesFromCart();
		return cart;
	}

	// If the selected shipping state is subject to an extra tax, there are 3 cases:
	// 1) already has correct fee 2) missing fee 3) has fee for wrong state

	const sku = _.get(StoreUtil.STATE_SHIPPING_FEES, state);
	const alreadyHasStateTaxItem = StoreUtil.hasItemWithSKU(cart.items, sku);
	if (!alreadyHasStateTaxItem) {
		// Check for any existing shipping fee that is not relevant to the given state.
		removeShippingFeesFromCart();

		// Add the correct state shipping fee as new item
		const response = addItem(cart, now, sku, 1, {});
		return response.cart;
	}
	return cart;
};

const areItemsEqual = function(item1, item2, ignoreBundleID) {
	return CartItemsUtil.areItemsEqual(item1, item2, ignoreBundleID);
};

// This function updates itemSuggestions, prices, and lastItemsUpdate. It also
// does other checks like unsetting paypal if that just became unavailable.
const postItemsUpdate = function(cart, now, updateSuggestions, user) {
	const newCartData = {
		prices: calculatePrices(cart),
		lastItemsUpdate: now,
	};
	if (updateSuggestions) {
		newCartData.itemSuggestions = computeSuggestions(cart, user);
	}
	cart = extendIn(cart, [], newCartData);
	if (!isPaypalAvailable(cart) && cart.onPaypalFlow) {
		cart = resetPaypal(cart);
	}
	return cart;
};

// itemsToAdd is an array of {sku, quantity, options} objects. Returns {cart,
// numAdditions, itemType, error, moreInfo}. The returned cart will be the same
// as the original if an error is present.
const addItemsAndCheck = function(cart, now, itemsToAdd, user) {
	const origCart = cart;
	const makeError = (error, moreInfo) => ({
		cart: origCart,
		numAdditions: 0,
		error,
		moreInfo,
	});
	const numAdditions = _.sum(itemsToAdd.map(i => i.quantity));
	if (numAdditions <= 0) {
		return makeError("noItems");
	}
	const isFullSet = StoreUtil.isFullSet(itemsToAdd[0]);
	const itemType = getTypeFromSKU(itemsToAdd[0].sku);

	for (let i = 0; i < itemsToAdd.length; i += 1) {
		const item = itemsToAdd[i];
		cart = setIn(cart, "items", items => {
			if (isFullSet) {
				const fullSetItems = BookstoreUtil.getBooksInFullSetFromSKU(
					itemsToAdd[0].sku
				);
				for (const bookItem of fullSetItems) {
					items = CartItemsUtil.adjustItem(
						items,
						bookItem.sku,
						itemsToAdd[0].quantity,
						bookItem.options
					);
				}
				return items;
			} else if (itemType === "bundle") {
				const displayName = item.options.displayName;
				const extraSubOptions = {};
				if (item.options.bookLevelDeferred) {
					extraSubOptions.bookLevelDeferred = true;
				}
				if (item.options.provisionalGrade != null) {
					extraSubOptions.provisionalGrade = item.options.provisionalGrade;
				}
				item.options.bundleIDs.forEach(b => {
					items = StoreBundleUtil.addBundle(
						items,
						displayName,
						b,
						item.quantity,
						extraSubOptions
					);
				});
				return items;
			} else {
				return CartItemsUtil.adjustItem(
					items,
					item.sku,
					item.quantity,
					item.options
				);
			}
		});
		if (StoreUtil.hasTooManySubscriptions(cart.items)) {
			return makeError("tooManySubscriptions");
		} else if (hasStackedSubscriptions(cart, item)) {
			// T23520: just silently deny the change given our guess for the cause.
			return makeError(null, null);
		} else if (hasReachedItemLimit(cart, item.sku)) {
			// TODO: View does not use the moreInfo field. We also used to send all
			// of the over-limit items.
			return makeError("PO", [item]);
		}
	}

	cart = postItemsUpdate(cart, now, true, user);
	return {cart, numAdditions, itemType, error: null, itemsAdded: itemsToAdd};
};

const addBooks = function(cart, now, additions, user) {
	const itemsToAdd = [];
	additions.forEach(a => {
		const skus = BookstoreUtil.getSKUs(a.type, a.bookHash, a.grade);
		const skuToItem = sku => ({sku, quantity: a.quantity, options: {}});
		itemsToAdd.push(...skus.map(skuToItem));
	});
	return addItemsAndCheck(cart, now, itemsToAdd, user);
};

const addSubscriptions = function(cart, now, additions, user) {
	const itemsToAdd = [];
	additions.forEach(a => {
		let sku;
		const options = {displayName: a.displayName};
		if (a.type === "bundle") {
			sku = "bundle";
			_.extend(options, {
				isBundle: true,
				bundleIDs: a.grade.map(g => "bundle-grade-" + g),
			});
		} else {
			sku = "beast-" + a.type;
		}
		itemsToAdd.push({sku, quantity: 1, options});
	});
	return addItemsAndCheck(cart, now, itemsToAdd, user);
};

const removeAllSubscriptions = function(cart, now, user) {
	cart = setIn(cart, "items", items => {
		return items.filter(item => {
			return !item.options.bundleID && !StoreUtil.isSubscriptionItem(item);
		});
	});
	cart = postItemsUpdate(cart, now, true, user);
	return cart;
};

const addItem = function(cart, now, sku, quantity, options, user) {
	return addItemsAndCheck(cart, now, [{sku, quantity, options}], user);
};

const addMultipleItems = function(cart, now, items, user) {
	return addItemsAndCheck(cart, now, items, user);
};

const removeItem = function(cart, now, itemToRemove, user) {
	let newCart = setIn(cart, "items", items => {
		if (itemToRemove.sku === "bundle") {
			const extraSubOptions = {};
			if (itemToRemove.options.bookLevelDeferred) {
				extraSubOptions.bookLevelDeferred = true;
			}
			if (itemToRemove.options.provisionalGrade != null) {
				extraSubOptions.provisionalGrade =
					itemToRemove.options.provisionalGrade;
			}
			itemToRemove.options.bundleIDs.forEach(b => {
				items = StoreBundleUtil.removeBundle(
					items,
					itemToRemove.options.displayName,
					b,
					itemToRemove.quantity,
					extraSubOptions
				);
			});
			return items;
		} else if (StoreUtil.isFullSet(itemToRemove)) {
			// Given the list of books to remove, iterate through them
			// We might have some of the books in the set added
			// individually that aren't grouped into a full set
			// so we should make sure that if we are removing fewer
			// books than there are total of that sku, then we
			// should adjust the quantity rather than completely
			// removing the item
			const booksToRemove = itemToRemove.subitems;
			for (const book of booksToRemove) {
				const totalNumBooks = getItemQuantity(cart, book.sku);
				const numToRemove = itemToRemove.quantity;
				if (numToRemove < totalNumBooks) {
					items = CartItemsUtil.updateItemQuantity(
						items,
						book,
						totalNumBooks - numToRemove
					);
				} else {
					items = CartItemsUtil.removeItem(items, book);
				}
			}
			return items;
		} else {
			const totalNumItems = getItemQuantity(
				cart,
				itemToRemove.sku,
				itemToRemove.options
			);
			if (itemToRemove.quantity < totalNumItems) {
				return CartItemsUtil.updateItemQuantity(
					items,
					itemToRemove,
					totalNumItems - itemToRemove.quantity
				);
			} else {
				return CartItemsUtil.removeItem(items, itemToRemove);
			}
		}
	});
	if (cart !== newCart) {
		newCart = postItemsUpdate(newCart, now, true, user);
	}
	return newCart;
};

const convertBundle = function(cart, now, subscriptionKey, bundleID, user) {
	let newCart = setIn(cart, "items", items =>
		StoreBundleUtil.convertBundle(items, subscriptionKey, bundleID)
	);
	if (cart !== newCart) {
		newCart = postItemsUpdate(newCart, now, true, user);
	}
	return newCart;
};

const convertSuggestedBundle = function(cart, now, user) {
	let newCart = setIn(cart, "items", items =>
		StoreBundleUtil.convertSuggestedBundle(items)
	);
	if (cart !== newCart) {
		newCart = postItemsUpdate(newCart, now, true, user);
	}
	return newCart;
};

const swapBundleGrade = function(cart, now, bundleItem, newBundleID, user) {
	let newCart = setIn(cart, "items", items => {
		const displayName = bundleItem.options.displayName;
		const oldBundleID = bundleItem.options.bundleIDs[0];
		items = StoreBundleUtil.removeBundle(items, displayName, oldBundleID);
		items = StoreBundleUtil.addBundle(items, displayName, newBundleID);
		return items;
	});
	if (cart !== newCart) {
		newCart = postItemsUpdate(newCart, now, true, user);
	}
	return newCart;
};

const updateItemQuantity = function(
	cart,
	now,
	itemToUpdate,
	newQuantity,
	user
) {
	let newCart = setIn(cart, "items", items => {
		return CartItemsUtil.updateItemQuantity(items, itemToUpdate, newQuantity);
	});
	if (cart !== newCart) {
		newCart = postItemsUpdate(newCart, now, false, user);
	}
	return newCart;
};

const updateItemOptions = function(cart, now, itemToUpdate, newOptions, user) {
	let newCart = setIn(cart, "items", items => {
		return CartItemsUtil.updateItemOptions(items, itemToUpdate, newOptions);
	});
	if (cart !== newCart) {
		newCart = postItemsUpdate(newCart, now, false, user);
	}
	return newCart;
};

const getNextPlaceholderName = function(cartItems) {
	const collapsedItems = StoreBundleUtil.getItemsWithBundles(cartItems, {
		skipValidation: true,
	});
	const existingNames = new Set(
		collapsedItems
			.filter(i => i.options && i.options.displayName)
			.map(i => i.options.displayName.toLowerCase())
	);
	for (let i = 0; ; i++) {
		const candidate = StoreUtil.getSubscriptionPlaceholderName(i);
		if (!existingNames.has(candidate.toLowerCase())) {
			return candidate;
		}
	}
};

const updateItemSku = function(cart, now, itemToUpdate, newSku, user) {
	let newCart = setIn(cart, "items", items => {
		return CartItemsUtil.updateItemSku(items, itemToUpdate, newSku);
	});
	if (cart !== newCart) {
		newCart = postItemsUpdate(newCart, now, true, user);
	}
	return newCart;
};

const setUserDidSetSubscribe = function(cart, value) {
	return setIn(cart, "userDidSetSubscribe", !!value);
};

const setSubscribeOnCheckout = function(cart, value) {
	return setIn(cart, "subscribeOnCheckout", !!value);
};

const refreshDefaultSubscribe = function(cart) {
	// if user inputted something, don't switch to the default
	if (cart.userDidSetSubscribe) {
		return;
	}

	// subscribe if shipping and billing are both in the US
	// works as shipping and billing both default to the US
	const defaultSubscribe =
		StoreUtil.isCountryUS(cart.form.data.shipping.country) &&
		StoreUtil.isCountryUS(cart.form.data.billing.country);

	// set the value in the cart
	return setSubscribeOnCheckout(cart, defaultSubscribe);
};

const setCoupon = function(cart, coupon) {
	// Important to do these separately since updatePrices uses cart.coupon.
	cart = setIn(cart, "coupon", coupon);
	cart = updatePrices(cart);
	return cart;
};

const clearCoupon = function(cart, {noMemory = false} = {}) {
	const oldCode = getCouponCode(cart);
	if (oldCode) {
		cart = noMemory ? cart : setIn(cart, "lastCouponCode", oldCode);
		cart = setCoupon(cart, null);
	}
	return cart;
};

const recheckCouponAfterItemsUpdate = function(cart, emitEvent) {
	if (cart.coupon) {
		const couponStatus = CouponUtil.appliesToCart(cart.coupon, cart.items);
		if (!couponStatus.ok) {
			cart = clearCoupon(cart);
			emitEvent("showOfficeModal", "couponRemovedByItemsUpdate");
		}
	}
	return cart;
};

const getCouponInfo = function(cart) {
	return {
		code: getCouponCode(cart),
		lastCouponCode: getLastCouponCode(cart),
	};
};

const getCouponExtraInfo = function(cart) {
	const couponObj = _.get(cart, ["coupon"]);
	if (!couponObj) return;
	const balance = Big(couponObj.balance);
	const prices = cart.prices;
	if (!prices) return;
	const subtotal = Big(prices.subtotal || 0);
	const applied = Big(prices.couponApplied || 0);
	return {
		couponUsed: !subtotal.eq(Big(0)),
		remainingBalance: balance.minus(applied),
		typeInfo: couponObj.typeInfo,
		lengthToExtend: couponObj.lengthToExtend,
	};
};

const getNumItems = function(cart) {
	if (!cart.items) {
		return 0;
	}
	return StoreUtil.getItemCount(cart.items);
};

const getNumItemsWithBundles = function(cart) {
	if (!cart.items) return 0;
	return StoreUtil.getItemCountWithBundles(cart.items);
};

const isEmpty = function(cart) {
	return cart.items.length === 0;
};

const getCouponCode = function(cart) {
	return _.get(cart, ["coupon", "code"], null);
};

const getLastCouponCode = function(cart) {
	return cart.lastCouponCode || null;
};

const getCurrentQuantity = function(cart, cartItem) {
	if (!cart || !cart.items || !cartItem) return null;
	const itemIndex = CartItemsUtil.findItemIndex(cart.items, cartItem);
	return itemIndex > -1 ? cart.items[itemIndex].quantity : 0;
};

// Helper to determine how cart items hav changed for bundling events, etc.
// Used for analytics.
const getItemsDiff = function(newItems, prevItems) {
	const unmatchedPrevItems = _.cloneDeep(prevItems);
	const itemsAdded = [];
	let itemsRemoved = [];
	_.each(newItems, newItem => {
		const matchIndex = _.findIndex(unmatchedPrevItems, prevItem =>
			areItemsEqual(prevItem, newItem, true)
		);
		if (matchIndex === -1) {
			itemsAdded.push(newItem);
		} else {
			const matchingPrevItem = unmatchedPrevItems[matchIndex];
			unmatchedPrevItems.splice(matchIndex, 1);
			const qtyChange = newItem.quantity - matchingPrevItem.quantity;
			const changeItem = _.cloneDeep(newItem);
			changeItem.quantity = Math.abs(qtyChange);
			if (qtyChange > 0) itemsAdded.push({changeItem});
			if (qtyChange < 0) itemsRemoved.push({changeItem});
			// If the quantity didn't change don't do anything
		}
	});
	itemsRemoved = itemsRemoved.concat(unmatchedPrevItems);
	return {itemsAdded, itemsRemoved};
};

//Wrappers for StoreUtil functions

const getTypeFromSKU = function(sku) {
	return StoreUtil.getTypeFromSKU(sku);
};

const getDefaultShippingType = function(country, state) {
	return StoreUtil.getDefaultShippingType(country, state);
};

//item, the item from the cart
//perItem, bool indicating whether to calculate the total for that item or for 1 of that item
const calculateItemPrices = function(cartItems, options) {
	return StoreUtil.calculateItemPrices(cartItems, options);
};

const getBookTypePriceString = function(
	type,
	quantity,
	bookHash,
	grade,
	options = {}
) {
	const skus = BookstoreUtil.getSKUs(type, bookHash, grade);
	const fakeItems = skus.map(sku => ({sku, quantity, options: {}}));
	const withPrices = calculateItemPrices(fakeItems, options);
	const total = withPrices.reduce((t, i) => t.plus(i.price), Big(0));
	return StringUtil.toPrice(total);
};

const fullSetShouldIncludeItemDiscount = function(fullSetItem, allItems) {
	// Get the subitems from the full set and check to see how many copies of each
	// of the books there are in total
	const fullSetBooks = fullSetItem.subitems;
	const fullSetBookQuantities = fullSetBooks.map(bookInfo => {
		const bookIdx = _.findIndex(allItems, b => b.sku === bookInfo.sku);
		if (bookIdx !== -1) {
			const totalQuantity = allItems[bookIdx].quantity;
			return {
				sku: bookInfo.sku,
				quantity: totalQuantity,
			};
		}

		return -1;
	});
	return _.some(fullSetBookQuantities, bookItemHasDiscount);
};

const itemHasDiscount = function(item, allItems) {
	if (StoreUtil.isFullSet(item)) {
		return fullSetShouldIncludeItemDiscount(item, allItems);
	} else if (StoreUtil.isBookItem(item)) {
		return bookItemHasDiscount(item);
	} else if (StoreUtil.isCodeItem(item)) {
		return codeItemHasDiscount(item);
	} else if (StoreUtil.isGiftItem(item)) {
		return giftItemHasDiscount(allItems);
	} else if (StoreUtil.isSubscriptionItem(item)) {
		const subscriptionItems = StoreUtil.getSubscriptionItems(allItems);
		if (subscriptionItems.length > 1) {
			const groupedSubscriptions = _.groupBy(subscriptionItems, i => i.sku);
			_.each(
				groupedSubscriptions,
				(subs, key) =>
					(groupedSubscriptions[key] = _.orderBy(subs, ["options.bundleID"]))
			);
			const itemGroup = groupedSubscriptions[item.sku];
			return (
				itemGroup.length > 1 && CartItemsUtil.findItemIndex(itemGroup, item) > 0
			);
		}
	} else if (StoreUtil.itemHasFullPrice(item)) {
		return true;
	} else {
		return false;
	}
};

const bookItemHasDiscount = function(item) {
	return item.totalQuantity >= 5 || item.quantity >= 5;
};

const codeItemHasDiscount = function(item) {
	return item.totalQuantity >= 5 || item.quantity >= 5;
};

const giftItemHasDiscount = function(allItems) {
	return allItems.filter(item => StoreUtil.isGiftItem(item)).length >= 5;
};

//TODO: More suggestions details could include
//-keeping track of items removed from the cart recently and having those mixed in
//-prioritizing by items more recently adjusted not just by when they were initially added
//-suggesting non-book items once we have them rather than filling in with first missing books
//-adding the ability here (and in view code) to suggest full grade sets or adding the rest of the books to complete a full grade set

// user is optional; when passed and OpsUser.isLoggedIn(user), BA Online is not suggested.
const shouldSuggestBaOnlineSubscription = function(cart, user) {
	const {items = []} = cart;
	const subscriptionItems = StoreUtil.getSubscriptionItems(items);
	if (subscriptionItems.length || StoreUtil.hasScienceItems(items)) {
		return false;
	}
	if (user && OpsUser.isLoggedIn(user)) {
		return false;
	}
	return true;
};

const computeSuggestions = function(cart, user) {
	let computedSuggestions = [];
	const {items = []} = cart;

	if (!items.length) {
		computedSuggestions = shouldSuggestBaOnlineSubscription(cart, user)
			? EMPTY_CART_SUGGESTIONS.slice()
			: _.without(EMPTY_CART_SUGGESTIONS, "ba-online");
	} else {
		let itemSuggestions = [];

		if (shouldSuggestBaOnlineSubscription(cart, user)) {
			itemSuggestions.unshift("ba-online");
		}

		// If there are no apparel items, always suggest a t-shirt
		// as the next item
		const apparelItems = StoreUtil.getApparelItems(items);
		if (!apparelItems.length) {
			itemSuggestions.push("shirt-beast-youth-small");
		}

		// Then we move on to book recommendations.
		// First we'll get all the books that are in the cart
		// and we will need to know which ones are puzzle books
		const allBooksInCart = StoreUtil.getSortedBookItems(items);
		const allBookLevelsInCart = _.uniq(
			allBooksInCart.map(bookWithSKU => {
				const {sku} = bookWithSKU;
				const {bookHash} = BookstoreUtil.getBookInfoFromSKU(sku);
				return BookstoreUtil.getGradeFromHash(bookHash);
			})
		);

		// For all books in the cart, bucket them by grade
		// then compare against the list of all known books in all grades
		// but checking only the grades whose books are also in the cart already
		const booksInCartByGrade = {};

		for (const book of allBooksInCart) {
			const {bookHash} = BookstoreUtil.getBookInfoFromSKU(book.sku);
			const grade = BookstoreUtil.getGradeFromHash(bookHash);
			booksInCartByGrade[grade] = booksInCartByGrade[grade] || [];
			booksInCartByGrade[grade].push({sku: book.sku, bookHash});
		}

		const allAvailableBooksByGrade = {};
		const allAvailableBookSKUs = _.uniq(BookstoreUtil.getAvailableBookSKUs());
		for (const bookSKU of allAvailableBookSKUs) {
			const {bookHash} = BookstoreUtil.getBookInfoFromSKU(bookSKU);
			const grade = BookstoreUtil.getGradeFromHash(bookHash);
			allAvailableBooksByGrade[grade] = allAvailableBooksByGrade[grade] || [];
			allAvailableBooksByGrade[grade].push({sku: bookSKU, bookHash});
		}

		// Now for the books that aren't in the cart, bucket them by grade
		// we will suggest books to complete sets in the lowest grade
		// they have in their cart (that is also not complete)
		const missingBooks = _.cloneDeep(allAvailableBooksByGrade);
		_.each(allAvailableBooksByGrade, (books, grade) => {
			const booksInCartForThisGrade = booksInCartByGrade[grade];
			missingBooks[grade] = _.differenceWith(
				books,
				booksInCartForThisGrade,
				(book1, book2) => book1.sku === book2.sku
			);
		});

		// Go through all the missing books, from lowest grade to highest grade
		// and pull out and recommend any science books they are missing, and
		// then any puzzle books they are missing.
		allBookLevelsInCart.forEach(gradeNum => {
			const missingBooksFromGrade = missingBooks[gradeNum];
			const missingScienceBooks = _.filter(
				missingBooksFromGrade,
				book => book.bookHash.indexOf("S") !== -1
			);
			missingScienceBooks.forEach(bookInfo => {
				itemSuggestions.push(bookInfo.sku);
				missingBooks[gradeNum] = _.filter(missingBooks[gradeNum], book => {
					return book.sku !== bookInfo.sku;
				});
			});
			const missingPuzzleBooks = _.filter(
				missingBooksFromGrade,
				book => book.bookHash.indexOf("puzzles") !== -1
			);
			missingPuzzleBooks.forEach(bookInfo => {
				itemSuggestions.push(bookInfo.sku);
				missingBooks[gradeNum] = _.filter(missingBooks[gradeNum], book => {
					return book.sku !== bookInfo.sku;
				});
			});
		});

		// For each book level that is in the cart, we will suggest
		// the next book in the sequence in order from the lowest grade in their
		// cart to the highest
		allBookLevelsInCart.forEach(gradeNum => {
			const missingBooksFromGrade = missingBooks[gradeNum].map(
				bookInfo => bookInfo.sku
			);
			itemSuggestions = itemSuggestions.concat(missingBooksFromGrade);
		});
		const allGrades = BookstoreUtil.getAvailableGrades();
		const levelsNotInCart = _.difference(allGrades, allBookLevelsInCart);

		// Finally, for each level not in the cart, suggest in the lowest
		// to the highest each book in sorted order
		levelsNotInCart.forEach(gradeStr => {
			const missingBooksFromGrade = missingBooks[gradeStr].map(
				bookInfo => bookInfo.sku
			);
			itemSuggestions = itemSuggestions.concat(missingBooksFromGrade);
		});

		// And lastly we only have 4 suggestions max, so cap at 4
		computedSuggestions = itemSuggestions.slice(0, 4);
	}
	return _.filter(
		computedSuggestions,
		sku => sku === "ba-online" || StoreUtil.isItemEnabled({sku})
	);
};

const refreshItemSuggestionsForUser = function(cart, user) {
	return extendIn(cart, [], {
		itemSuggestions: computeSuggestions(cart, user),
	});
};

const getSuggestions = function(cart) {
	return cart.itemSuggestions;
};

const getFormSection = function(cart, section) {
	return OpsCheckoutForm.getSection(cart.form, section);
};

const checkFormSection = function(cart, section) {
	return OpsCheckoutForm.checkSection(cart.form, section);
};

const validateSectionWithMissing = function(cart, section) {
	return setIn(cart, "form", f =>
		OpsCheckoutForm.validateSection(f, section, {
			addMissing: true,
			needsRecurringBilling: needsRecurringBilling(cart),
		})
	);
};

const isFormSectionComplete = function(cart, section, options) {
	return OpsCheckoutForm.isSectionComplete(cart.form, section, options);
};

const isPaypalAvailable = function(cart) {
	return !isEmpty(cart) && !StoreUtil.hasSubscriptionItems(cart.items);
};

const setPaypalModalData = function(cart, paymentID, payerID) {
	return extendIn(cart, [], {
		onPaypalFlow: true,
		paypalPaymentID: paymentID,
		paypalPayerID: payerID,
	});
	// PM and SL were considering setting addressVerified to true here, since
	// paypal does address validation for us. Decided it was safer not to.
};

const resetPaypal = function(cart) {
	return extendIn(cart, [], {
		onPaypalFlow: false,
		paypalPaymentID: null,
		paypalPayerID: null,
	});
};

const isReadyToVerifyNewAccount = function(cart) {
	return (
		!cart.onPaypalFlow &&
		isFormSectionComplete(cart, "account") &&
		!getFormSection(cart, "account").exists
	);
};

const isVerifyingAccount = function(cart) {
	return cart.isVerifyingAccount;
};

const lockCheckoutForLoggedIn = function(cart, user) {
	if (!OpsUser.isLoggedIn(user)) return false;
	// Handle logged in users
	// Is a current BA student
	if (OpsUser.hasRole(user, "student")) return true;
	// Is a current BA user (initialized or not) and has subscriptions
	// in cart
	if (
		!OpsUser.isPlatformGuest(user) &&
		StoreUtil.hasSubscriptionItems(cart.items)
	)
		return true;
	// Is not a BA user but is a restricted platform user
	if (OpsUser.isPlatformGuest(user) && OpsUser.isRestrictedPlatformUser(user))
		return true;
	// Everyone else is fine.
	return false;
};

const copyUserIntoAccountForm = function(cart, user) {
	let newAccountVals;
	if (user && OpsUser.isLoggedIn(user)) {
		newAccountVals = {
			ownerID: user.userID,
			email: user.model.email,
			password: "",
			passwordRepeat: "",
			exists: true,
			remember: false,
		};
	} else if (cart.form.data.account.ownerID) {
		newAccountVals = {
			ownerID: null,
			email: "",
			password: "",
			passwordRepeat: "",
			exists: false,
			remember: false,
		};
	}
	if (newAccountVals) {
		cart = setIn(cart, ["form", "data", "account"], newAccountVals);
		cart = setIn(cart, ["accountVerified"], !!newAccountVals.ownerID);
	}
	return cart;
};

// Assumes account form has been validated.
const getAccountFinishingAction = function(cart) {
	const account = getFormSection(cart, "account");
	if (account.ownerID) {
		return null; // Use default view behavior of advancing to next step.
	} else {
		return {
			type: AT.VERIFY_NEW_ACCOUNT_REQUEST,
			email: account.email,
			password: account.password,
			passwordRepeat: account.passwordRepeat,
		};
	}
};

const isReadyToPrepareOrder = function(cart) {
	if (isClassWalletFlow(cart)) {
		return isFormSectionComplete(cart, "shipping", {methodOnly: true});
	}

	if (!isFormSectionComplete(cart, getPrepareAddressSection(cart))) {
		return false;
	}
	return !cart.onPaypalFlow || isFormSectionComplete(cart, "contact");
};

const needsToVerifyAddress = function(cart) {
	const addressInfo = getFormSection(cart, getPrepareAddressSection(cart));
	return (
		needsShipping(cart) &&
		!cart.addressVerified &&
		StoreUtil.canVerifyAddress(addressInfo)
	);
};

const needsToCalculateTaxes = function(cart) {
	return cart.prices.tax === null;
};

const hasAlreadyPreparedOrder = function(cart) {
	return !needsToVerifyAddress(cart) && !needsToCalculateTaxes(cart);
};

const isReadyToPlaceOrder = function(cart) {
	return getAvailableCheckoutSteps(cart).indexOf("review") > -1;
};

const needsShipping = function(cart) {
	return StoreUtil.hasShippableItems(cart.items);
};

const needsShippingToBlockedAddress = function(cart) {
	const shipping = getFormSection(cart, "shipping");
	return needsShipping(cart) && !StoreUtil.isAddressShippable(shipping);
};

const needsRecurringBilling = function(cart) {
	const cartItems = StoreBundleUtil.getItemsWithBundles(cart.items);
	return StoreUtil.hasSubscriptionItems(cartItems);
};

const shouldSkipPhoneValidation = function(cart) {
	return StoreUtil.hasSubscriptionItems(cart.items);
};

const getPrepareAddressSection = function(cart) {
	return needsShipping(cart) ? "shipping" : "billing";
};

const isClassWalletFlow = function(cart) {
	return !!cart.isClassWalletFlow;
};

const getAllCheckoutSteps = function(cart) {
	if (cart.isClassWalletFlow) {
		return ["cart", "review"];
	} else if (cart.onPaypalFlow) {
		return ["cart", "paypalinfo", "review"];
	} else if (isEmpty(cart)) {
		return ["cart", "shipping", "billing", "review"];
	} else {
		const includeShipping = needsShipping(cart);
		const includeAccount = StoreUtil.hasSubscriptionItems(cart.items);
		return [
			"cart",
			includeAccount && "account",
			includeShipping && "shipping",
			"billing",
			"review",
		].filter(Boolean);
	}
};

// Internal helper for getAvailableCheckoutSteps, which considers a step
// available if all previous steps are complete.
const isStepComplete = function(cart, step) {
	if (step === "cart") {
		return !isEmpty(cart) && !StoreBundleUtil.canConvertBundle(cart.items);
	} else if (step === "account") {
		return cart.accountVerified;
	} else if (step === "shipping") {
		return (
			isFormSectionComplete(cart, "shipping") &&
			!needsShippingToBlockedAddress(cart) &&
			hasAlreadyPreparedOrder(cart)
		);
	} else if (step === "billing") {
		return (
			isFormSectionComplete(cart, "billing") &&
			isFormSectionComplete(cart, "creditCard") &&
			isFormSectionComplete(cart, "contact") &&
			hasAlreadyPreparedOrder(cart)
		);
	} else if (step === "paypalinfo") {
		const addressDone = needsShipping(cart)
			? isFormSectionComplete(cart, "shipping")
			: isFormSectionComplete(cart, "billing");
		return (
			addressDone &&
			isFormSectionComplete(cart, "contact") &&
			hasAlreadyPreparedOrder(cart)
		);
	}
	return true;
};

// Internal helper for getAvailableCheckoutSteps. Adds extra checks for a step
// to be available in addition to previous steps being complete.
const isStepAvailable = function(cart, user, step) {
	if (cart.isClassWalletFlow && step === "review") {
		// Must have entered shipping method
		const check = OpsCheckoutForm.checkSection(cart.form, "shipping", {
			methodOnly: true,
		});
		return check.missing.length === 0;
	} else if (step === "paypalinfo") {
		return cart.paypalPaymentID && cart.paypalPayerID;
	} else if (user && step === "account") {
		return !OpsUser.isLoggedIn(user) || OpsUser.isPlatformGuest(user);
	}
	return true;
};

// Returns a prefix of getAllCheckoutSteps. Always nonempty.
// When currentStep is "cart", only steps up to and including cart are returned
// so the user must click Continue to leave the cart step.
const getAvailableCheckoutSteps = function(cart, user, currentStep) {
	const allSteps = getAllCheckoutSteps(cart);
	let i = 1; // Always include the first step.
	while (
		i < allSteps.length &&
		isStepComplete(cart, allSteps[i - 1]) &&
		isStepAvailable(cart, user, allSteps[i])
	) {
		i += 1;
	} // i is now the index of the first unavailable step
	let availableSteps = allSteps.slice(0, i);
	if (currentStep === "cart") {
		const cartIndex = allSteps.indexOf("cart");
		availableSteps = availableSteps.filter(
			s => allSteps.indexOf(s) <= cartIndex
		);
	}
	return availableSteps;
};

const getPrepareStep = function(cart) {
	const steps = getAllCheckoutSteps(cart);
	return _.find(
		steps,
		s =>
			s === "paypalinfo" ||
			s === "shipping" ||
			s === "billing" ||
			(cart.isClassWalletFlow && s === "cart")
	);
};

const getNextStep = function(cart, currentStep) {
	const steps = getAllCheckoutSteps(cart);
	const index = steps.indexOf(currentStep);
	return steps[index + 1];
};

const hasPassedStep = function(cart, stepToCheck, currentStep) {
	const allSteps = getAllCheckoutSteps(cart);
	const currIndex = allSteps.indexOf(currentStep);
	const checkIndex = allSteps.indexOf(stepToCheck);
	return currIndex > -1 && checkIndex > -1 && currIndex > checkIndex;
};

const getStepFormSections = function(cart, step) {
	if (step === "account") {
		return ["account"];
	} else if (step === "shipping") {
		return ["shipping"];
	} else if (step === "paypalinfo") {
		if (needsShipping(cart)) {
			return ["shipping", "contact"];
		} else {
			return ["billing", "contact"];
		}
	} else if (step === "billing") {
		return ["billing", "creditCard", "contact"];
	} else if (step === "review") {
		return ["contact"];
	}
	return null;
};

const getGroupedValidation = function(cart, formTypes) {
	return OpsCheckoutForm.getGroupedValidation(cart.form, formTypes);
};

const getGroupedStepValidation = function(cart, step) {
	return getGroupedValidation(cart, getStepFormSections(cart, step));
};

const shouldCopyShipping = function(cart) {
	return cart.form.shippingForBilling;
};

const getShippingMethod = function(cart) {
	return cart.form.data.shipping.method;
};

const updateShouldCopyShipping = function(cart, shouldCopy) {
	return setIn(cart, "form", f => {
		if (!shouldCopy) {
			f = OpsCheckoutForm.copyShippingToBilling(f);
		}
		return OpsCheckoutForm.setShippingAsBilling(f, shouldCopy, {
			shouldSkipPhoneValidation: shouldSkipPhoneValidation(cart),
		});
	});
};

const getShownTotal = function(cart) {
	const prices = cart.prices || {};
	return StoreUtil.calculateTotal(prices);
};

const computeTotal = function(prices) {
	if (prices.subtotal && prices.tax) {
		return StoreUtil.calculateTotal(prices);
	}
	return null;
};

const calculateCouponApplied = function(cart) {
	const totals = StoreUtil.getTotalItemPrices(cart.items, cart.coupon);
	// The second parameter to round() tells it whether to round up, down, etc.
	// Passing in 3 tells it to round up. https://mikemcl.github.io/big.js/#round
	const couponRounded = totals.applied.round(2, 3);
	return couponRounded.toFixed(2);
};

const calculateShippingPriceWithMethod = function(cart, method) {
	method = method || getShippingMethod(cart);
	if (!method) {
		return null;
	}
	return StoreUtil.calculateShipping(cart.items, method).toFixed(2);
};

const calculatePrices = function(cart) {
	const subtotal = StoreUtil.calculateSubtotal(cart.items).toFixed(2);
	const couponApplied = calculateCouponApplied(cart);
	const shipping = calculateShippingPriceWithMethod(cart);
	const tax = null; // We don't know this until we ping Avalara
	const prices = {subtotal, couponApplied, shipping, tax};
	prices.total = computeTotal(prices);
	return prices;
};

const updateTax = function(prices, tax) {
	const newPrices = _.extend({}, prices, {tax});
	newPrices.total = computeTotal(newPrices);
	return newPrices;
};

const updatePrices = function(cart) {
	return setIn(cart, ["prices"], p => calculatePrices(cart));
};

const setCaptchaResponse = function(cart, captchaResponse) {
	return setIn(cart, "captchaResponse", captchaResponse);
};

const onPageChange = function(cart, isOnCheckout, now) {
	if (!cart || isOnCheckout) {
		return cart;
	}
	return setIn(
		cart,
		"form",
		OpsCheckoutForm.resetSection(cart.form, "creditCard", now)
	);
};

//Gets quantity of single item in cart
const getItemQuantity = function(cart, sku, options = {}) {
	const cartItems = cart.items;
	const itemIndex = CartItemsUtil.findItemIndex(cartItems, {sku, options});
	if (itemIndex > -1) {
		return cartItems[itemIndex].quantity;
	} else {
		return 0;
	}
};

const hasStackedSubscriptions = function(cart, item) {
	const displayName = _.get(item, "options.displayName");
	if (!displayName) {
		return false;
	}
	return cart.items.some(i => {
		return i.options && i.options.displayName === displayName && i.quantity > 1;
	});
};

const hasReachedItemLimit = function(cart, sku, options) {
	return getItemQuantity(cart, sku, options) > ITEM_LIMIT;
};

const getNumSubscriptions = function(cartItems) {
	return _.filter(cartItems, i => StoreUtil.isSubscriptionItem(i)).length;
};

const getNumSubscriptionsByType = function(cartItems) {
	const bundledItems = StoreBundleUtil.getItemsWithBundles(cartItems);
	const subscriptions = _.countBy(
		StoreUtil.getSubscriptionItems(bundledItems),
		i => i.sku
	);
	return {
		monthly: subscriptions["beast-monthly"] || 0,
		yearly: subscriptions["beast-yearly"] || 0,
		bundle: StoreUtil.getBundleItems(bundledItems).length,
	};
};

const hasNonBundleBookItems = function(cartItems) {
	return StoreUtil.hasBookItems(StoreBundleUtil.getUnbundledItems(cartItems));
};

// Arguments past the first are optional and only used for diagnostics.
// Returns a filtered cart items array with only valid items.
const fixCartItems = function(cartItems, sourceCart) {
	if (!cartItems) return [];
	cartItems = cartItems.filter((item, index) => {
		// isItemEnabled returns false without throwing for bad skus.
		if (!StoreUtil.isItemEnabled(item)) {
			ErrorUtil.log("E_CART_ITEM", "Cart had an invalid item", {
				item,
				index,
				allItems: cartItems,
				cartID: sourceCart && sourceCart.cartID,
				userID: sourceCart && sourceCart.userID,
				sourceItems: sourceCart && sourceCart.items,
			});
			return false;
		}
		return true;
	});
	cartItems = StoreBundleUtil.fixItems(cartItems);
	return cartItems;
};

module.exports = {
	init,
	clearNonItems,
	fixCart,
	shouldBlockPage,
	updateShippingAddressFromPaypal,
	updateFormData,
	addExtraCartItems,
	addBooks,
	addSubscriptions,
	removeAllSubscriptions,
	addItem,
	addMultipleItems,
	removeItem,
	updateItemQuantity,
	updateItemOptions,
	updateItemSku,
	setUserDidSetSubscribe,
	setSubscribeOnCheckout,
	refreshDefaultSubscribe,
	setCoupon,
	clearCoupon,
	recheckCouponAfterItemsUpdate,
	getDefaultShippingType,
	getNumItems,
	getNumItemsWithBundles,
	areItemsEqual,
	isEmpty,
	getCouponCode,
	getLastCouponCode,
	getCouponInfo,
	getCouponExtraInfo,
	getTypeFromSKU,
	getCurrentQuantity,
	getItemsDiff,
	calculateItemPrices,
	itemHasDiscount,
	bookItemHasDiscount,
	codeItemHasDiscount,
	getBookTypePriceString,
	getSuggestions,
	getShippingMethod,
	updateShouldCopyShipping,
	updatePrices,
	updateTax,
	getFormSection,
	checkFormSection,
	validateSectionWithMissing,
	isFormSectionComplete,
	setCaptchaResponse,
	isPaypalAvailable,
	setPaypalModalData,
	resetPaypal,
	isReadyToVerifyNewAccount,
	isVerifyingAccount,
	getAccountFinishingAction,
	copyUserIntoAccountForm,
	lockCheckoutForLoggedIn,
	getPrepareAddressSection,
	isReadyToPrepareOrder,
	needsToVerifyAddress,
	hasAlreadyPreparedOrder,
	isReadyToPlaceOrder,
	shouldCopyShipping,
	getStepFormSections,
	getGroupedValidation,
	getGroupedStepValidation,
	computeSuggestions,
	refreshItemSuggestionsForUser,
	calculateShippingPriceWithMethod,
	calculatePrices,
	onPageChange,
	needsShipping,
	needsShippingToBlockedAddress,
	needsRecurringBilling,
	shouldSkipPhoneValidation,
	isClassWalletFlow,
	getAllCheckoutSteps,
	getAvailableCheckoutSteps,
	getPrepareStep,
	getNextStep,
	hasPassedStep,
	getShownTotal,
	ITEM_LIMIT,
	getNumSubscriptions,
	getNumSubscriptionsByType,
	hasNonBundleBookItems,
	fixCartItems,
	convertBundle,
	convertSuggestedBundle,
	swapBundleGrade,
	getNextPlaceholderName,
	getItemPriceBig: StoreUtil.getItemPriceBig,
	getItemPriceString: StoreUtil.getItemPriceString,
	getMultiBundleName: StoreUtil.getMultiBundleName,
	getUpgradeItemName: StoreUtil.getUpgradeItemName,
	getSubscriptionNames: StoreUtil.getSubscriptionNames,
	calculateSubtotal: StoreUtil.calculateSubtotal,
	getNameFromSKU: StoreUtil.getNameFromSKU,
	isBookItem: StoreUtil.isBookItem,
	isFullSet: StoreUtil.isFullSet,
	isBundleItem: StoreUtil.isBundleItem,
	isSubscriptionItem: StoreUtil.isSubscriptionItem,
	hasSubscriptionItems: StoreUtil.hasSubscriptionItems,
	hasBundleItems: StoreUtil.hasBundleItems,
	hasBookItems: StoreUtil.hasBookItems,
	hasApparelItems: StoreUtil.hasApparelItems,
	hasCodeItems: StoreUtil.hasCodeItems,
	hasGiftItems: StoreUtil.hasGiftItems,
	hasOnlyCodeItems: StoreUtil.hasOnlyCodeItems,
	hasOnlySubscriptionCodeItems: StoreUtil.hasOnlySubscriptionCodeItems,
	getItemsWithBundles: StoreBundleUtil.getItemsWithBundles,
	getUpgradeItemsWithBundles: StoreBundleUtil.getUpgradeItemsWithBundles,
	getItemName: StoreUtil.getItemName,
	getItemType: StoreUtil.getItemType,
	getItemsWithSKU: StoreUtil.getItemsWithSKU,
	getSortedBookItems: StoreUtil.getSortedBookItems,
	getSortedSubscriptionItems: StoreUtil.getSortedSubscriptionItems,
	getSortedBundleItems: StoreUtil.getSortedBundleItems,
	getSortedApparelItems: StoreUtil.getSortedApparelItems,
	getSortedCodeItems: StoreUtil.getSortedCodeItems,
	getSortedGiftItems: StoreUtil.getSortedGiftItems,
	groupBooksIntoSets: BookstoreUtil.groupBooksIntoSets,
	getBundleInfo: StoreBundleUtil.getBundleInfo,
	getBundleSubitems: StoreUtil.getBundleSubitems,
	getBundleSubscriptions: StoreBundleUtil.getBundleSubscriptions,
	calculateBundlePrice: StoreUtil.calculateBundlePrice,
	canConvertBundle: StoreBundleUtil.canConvertBundle,
	canSuggestBundle: StoreBundleUtil.canSuggestBundle,
	getUnbundledSubscriptions: StoreBundleUtil.getUnbundledSubscriptions,
	getUnbundledSubscriptionCodes: StoreBundleUtil.getUnbundledSubscriptionCodes,
	getUnbundledBookSets: StoreBundleUtil.getUnbundledBookSets,
	getOrderedUnbundledSubscriptions:
		StoreBundleUtil.getOrderedUnbundledSubscriptions,
	getOrderedUnbundledBooks: StoreBundleUtil.getOrderedUnbundledBooks,
	getShippingFeeItemData: StoreUtil.getShippingFeeItemData,
};
